import { isNil } from '@lucidlink/tscore';
import { WebFsError } from '#common/types';
import { getFetchErrorMessage } from './fetch-error-handler';
import { HttpMetadataFetchError, HttpMetadataNetworkError } from './http-metadata-fetch-error';
import { parseChecksum } from './http-response';
import { HTTP_STATUS_PARTIAL_CONTENT, HTTP_STATUS_RANGE_NOT_SATISFIABLE } from './http-status-codes';
const DEFAULT_TIMEOUT_MS = 30_000;
export async function fetchHttpFileMetadata(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options?.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    try {
        return ((await fetchWithHeadRequest(options?.headUrl ?? url, controller.signal)) ??
            (await fetchWithRangeRequest(url, controller.signal, options?.onUnexpectedHttpStatus)));
    }
    catch (error) {
        if (error instanceof WebFsError) {
            throw error;
        }
        throw new HttpMetadataFetchError(getFetchErrorMessage(error));
    }
    finally {
        clearTimeout(timeoutId);
    }
}
// HEAD may fail on GET-only presigned URLs (e.g. S3); only trust it on success, otherwise fall back to a range GET.
async function fetchWithHeadRequest(url, signal) {
    let response;
    try {
        response = await fetchOrThrow(url, { method: 'HEAD', signal });
        if (response.ok && !isNil(response.headers.get('content-length'))) {
            return parseFullResponse(response);
        }
        return undefined;
    }
    catch (error) {
        if (signal.aborted) {
            throw error;
        }
        return undefined;
    }
    finally {
        await response?.body?.cancel().catch(() => { });
    }
}
async function fetchWithRangeRequest(url, signal, onUnexpectedHttpStatus) {
    let response;
    try {
        response = await fetchOrThrow(url, { headers: { Range: 'bytes=0-0' }, signal });
        if (response.status === HTTP_STATUS_PARTIAL_CONTENT) {
            return parsePartialContentResponse(response);
        }
        if (response.status === HTTP_STATUS_RANGE_NOT_SATISFIABLE) {
            await response.body?.cancel().catch(() => { });
            response = undefined;
            return fetchFullResponse(url, signal, onUnexpectedHttpStatus);
        }
        if (response.ok) {
            return parseFullResponse(response);
        }
        return await handleUnexpectedStatus(response, onUnexpectedHttpStatus);
    }
    finally {
        await response?.body?.cancel().catch(() => { });
    }
}
async function handleUnexpectedStatus(response, onUnexpectedHttpStatus) {
    if (!isNil(onUnexpectedHttpStatus)) {
        await onUnexpectedHttpStatus(response);
    }
    throw new HttpMetadataFetchError(`Unexpected HTTP status: ${response.status} ${response.statusText}`);
}
function parsePartialContentResponse(response) {
    const contentRange = response.headers.get('content-range');
    const sizeString = contentRange?.split('/')[1];
    if (isNil(sizeString) || sizeString === '*') {
        throw new HttpMetadataFetchError('Missing or invalid Content-Range header');
    }
    return {
        size: parseBigInt(sizeString, 'Content-Range size'),
        checksum: parseChecksum(response),
    };
}
function parseFullResponse(response) {
    const contentLength = response.headers.get('content-length');
    if (isNil(contentLength)) {
        throw new HttpMetadataFetchError('Missing Content-Length header');
    }
    return {
        size: parseBigInt(contentLength, 'Content-Length'),
        checksum: parseChecksum(response),
    };
}
async function fetchFullResponse(url, signal, onUnexpectedHttpStatus) {
    let response;
    try {
        response = await fetchOrThrow(url, { signal });
        if (response.ok) {
            return parseFullResponse(response);
        }
        return await handleUnexpectedStatus(response, onUnexpectedHttpStatus);
    }
    finally {
        await response?.body?.cancel().catch(() => { });
    }
}
async function fetchOrThrow(url, init) {
    try {
        return await fetch(url, init);
    }
    catch (error) {
        throw new HttpMetadataNetworkError(getFetchErrorMessage(error));
    }
}
function parseBigInt(value, context) {
    try {
        return BigInt(value);
    }
    catch {
        throw new HttpMetadataFetchError(`Invalid ${context}: "${value}" is not a valid integer`);
    }
}
